import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { updateMeasurement } from '../../services/metrics.service';
import { MeasurementHistoryItem, UpdateMeasurementPayload } from '../../types/metrics';

interface EditMeasurementDialogProps {
  measurement: MeasurementHistoryItem | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditMeasurementDialog: React.FC<EditMeasurementDialogProps> = ({ measurement, open, onClose, onSuccess }) => {
  const [value, setValue] = useState('');
  const [measuredAt, setMeasuredAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (measurement) {
      setValue(measurement.value.toString());
      // Format date for datetime-local input
      const localDate = new Date(measurement.measuredAt).toISOString().slice(0, 16);
      setMeasuredAt(localDate);
    } else {
      // Reset form when there is no measurement
      setValue('');
      setMeasuredAt('');
    }
  }, [measurement]);

  const handleSubmit = async () => {
    if (!measurement) return;

    const payload: UpdateMeasurementPayload = {
      value: Number(value),
      measuredAt: new Date(measuredAt).toISOString(),
    };

    try {
      setSubmitting(true);
      setError(null);
      await updateMeasurement(measurement.id, payload);
      onSuccess();
    } catch (err) {
      setError('Помилка при оновленні вимірювання.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!measurement) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Редагувати вимірювання</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {measurement.measurementType.name}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label={`Значення (${measurement.unit.symbol})`}
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Дата та час вимірювання"
            type="datetime-local"
            value={measuredAt}
            onChange={(e) => setMeasuredAt(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Скасувати</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
